import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AgentAdoptionResponse } from '../../../api/agent-miner-client/agent-miner-client.type';
import { Table } from '../../../components/table/table';
import { DEFAULT_PAGE_SIZE } from '../../../components/table/table.constants';
import { getAdoptionTeamsColumns } from './adoption-teams-table.columns';
import { buildAdoptionTeamTableRows } from './adoption.helpers';
import type { AdoptionTeamTableRow } from './adoption.helpers';

type Props = {
  response: AgentAdoptionResponse;
};

/**
 * The Teams scorecard: one row per team, sorted by sessions descending by
 * default. Cross-team sessions fan out to every linked team, so the rows
 * deliberately have no summable total — org figures live in the KPI strip.
 *
 * @param {Props} props - Component props
 * @returns {JSX.Element} The rendered table
 */
export const AdoptionTeamsTable = ({ response }: Props) => {
  const { t } = useTranslation();
  const rows = useMemo(() => buildAdoptionTeamTableRows(response), [response]);
  const columns = useMemo(() => getAdoptionTeamsColumns(t), [t]);
  const [currentPage, setCurrentPage] = useState(1);

  // A team filter change can shrink the row set below the current page's start;
  // clamp back into range so the user never lands on an empty page.
  const pageCount = Math.max(1, Math.ceil(rows.length / DEFAULT_PAGE_SIZE));
  useEffect(() => {
    if (currentPage > pageCount) setCurrentPage(pageCount);
  }, [currentPage, pageCount]);

  return (
    <Table<AdoptionTeamTableRow>
      data={rows}
      columns={columns}
      getRowId={(row) => row.id}
      defaultSortedBy="sessions"
      defaultSortedByDirection="desc"
      totalItems={rows.length}
      currentPage={currentPage}
      onPageChange={setCurrentPage}
      stickyHeader
    />
  );
};
